import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Grid,
  Typography,
  Stepper,
  Box,
} from '@mui/material';
import { ExpandMoreIcon } from 'components/common/Icon';
import React, { useEffect, useMemo, useReducer, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CheckCircle } from '@mui/icons-material';
import {
  FormationsPlan,
  IAccount,
  ProgressTrackerStages,
  ProgressTrackerStatus,
} from 'models/account';
import { useCurrentAccount, useCurrentCompany } from 'hooks/api';
import {
  isBusinessBusinessInABox,
  getCompletedCount,
  getFirstNoCompleted,
} from 'components/ProgressTracker/util';
import { FormationsStep } from 'components/FormationsStep';
import { BUSINESS_OWNERSHIP } from 'enums';
import { Business } from 'models/business';
import { Routes } from 'fnRoutes';
import { PersonalDetailsContainer as PersonalDetails } from './PersonalDetails';
import { CompanyDetailsContainerBusinessInABox as CompanyDetailsBusinessInABox } from './CompanyDetailsBusinessInABox';
import { OwnershipDetailsContainer as OwnershipDetails } from './OwnershipDetails';
import { AddressInformationContainer as AddressInformation } from './AddressInformation';
import { initialState, reducer } from './state';
import { StageSkeleton } from '../StageSkeleton';
import { AccordionCTA } from '../AccordionCTA';
import { FilingDocumentsBusinessInABox } from './FilingDocumentsBusinessInABox';
import { StepContentWrapper } from '../Components/StepContentWrapper';
import { StageStatusColumn, StageStatusText } from '../Components/StageStatusColumn';

interface TParams {
  business: Business;
  isDesktop: boolean;
  account: IAccount;
  expanded: boolean | string;
  handleExpand: (
    panel: string,
  ) => (event: React.ChangeEvent<{}>, isExpanded: boolean) => void;
  onContinueToAccountVerification: () => void;
}

const IncorporationLayout = ({
  business,
  isDesktop,
  account: accData,
  expanded,
  handleExpand,
  onContinueToAccountVerification,
}: TParams) => {
  const { t } = useTranslation();
  const isBIB = isBusinessBusinessInABox(business);
  const isMultiMemberOwnedBusiness =
    business?.businessOwnership === BUSINESS_OWNERSHIP.PARTNERSHIP;

  const [state, dispatch] = useReducer(
    reducer,
    initialState(business?.entityType ?? '', isMultiMemberOwnedBusiness, isBIB),
  );

  const completed = useMemo(() => {
    const localStorageCompleted =
      localStorage.getItem('filingDocumentsCompletedBIB') === 'true';
    const localStorageIncrement =
      localStorageCompleted &&
      state[ProgressTrackerStages.FilingDocuments]?.status !==
        ProgressTrackerStatus.Completed
        ? 1
        : 0;
    return getCompletedCount(state, true) + localStorageIncrement;
  }, [state, localStorage.getItem('filingDocumentsCompletedBIB')]);

  const [activeStep, setActiveStep] = useState(0);

  const onBackClick = () => {
    if (activeStep > 0) {
      setActiveStep((prev) => prev - 1);
    }
  };

  useEffect(() => {
    const firstNotCompleted = getFirstNoCompleted(state, true);
    if (firstNotCompleted !== -1) {
      setActiveStep(firstNotCompleted);
    }
  }, [state]);

  useEffect(() => {
    if (business?.progress?.length > 0) {
      dispatch({
        type: ProgressTrackerStages.PersonalDetails,
        value: business?.progress,
      });
      dispatch({
        type: ProgressTrackerStages.CompanyDetails,
        value: business?.progress,
      });
      dispatch({
        type: ProgressTrackerStages.AddressDetails,
        value: business?.progress,
      });
      if (isMultiMemberOwnedBusiness) {
        dispatch({
          type: ProgressTrackerStages.OwnershipDetails,
          value: business?.progress,
        });
      }
      dispatch({
        type: ProgressTrackerStages.FilingDocuments,
        value: business?.progress,
      });
    }
  }, [business, isMultiMemberOwnedBusiness]);

  const steps = Object.keys(state);
  const stageCompleted = useMemo(
    () => completed === steps.length,
    [completed, steps],
  );

  return (
    <Accordion
      expanded={expanded === 'incorporation'}
      onChange={handleExpand('incorporation')}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        aria-controls="incorporation-content"
        id="incorporation-header"
        sx={{
          alignItems: 'flex-start',
        }}
      >
        <Grid container>
          <Grid item xs={10}>
            <Typography variant="h6B">Incorporate Your Business</Typography>
            <Typography variant="subtitle1" color="primary">
              {!stageCompleted &&
                t('progressTracker.incorporation.businessInABoxSubtitle')}
              {stageCompleted && (
                <Box>
                  {t(
                    'progressTracker.incorporation.incorporationCompletedInfo',
                  )}
                  &nbsp;
                </Box>
              )}
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              {t('progressTracker.incorporation.processingTime')}
            </Typography>
            <AccordionCTA
              expanded={expanded}
              handleExpand={handleExpand}
              completed={completed}
              totalStep={steps.length}
              testId="start-incorporation"
              accordionId="incorporation"
            />
          </Grid>

          <StageStatusColumn item xs={2}>
            <StageStatusText variant="h8B">
              {stageCompleted && <CheckCircle color="secondary" />}
              {completed}/{steps.length}
            </StageStatusText>
          </StageStatusColumn>
        </Grid>
      </AccordionSummary>
      <AccordionDetails>
        <Grid container spacing={{ md: 4 }}>
          <Grid
            item
            xs={12}
            md={3}
          >
            <Stepper
              activeStep={activeStep}
              nonLinear
              orientation={isDesktop ? 'vertical' : 'horizontal'}
              alternativeLabel={!isDesktop}
              data-testid="incorporation-stages"
            >
              {steps.map((key, index) => (
                <FormationsStep
                  key={key}
                  stageKey={key}
                  index={index}
                  setActiveStep={setActiveStep}
                  status={state[key]?.status}
                  label={state[key]?.label}
                  isTrainer={false}
                  completedSteps={completed}
                  totalSteps={steps.length}
                  activeStep={activeStep}
                  typeOfAccount={FormationsPlan.BIB}
                />
              ))}
            </Stepper>
          </Grid>
          <StepContentWrapper
            item
            xs={12}
            md={9}
          >
            {activeStep === 0 && <PersonalDetails business={business} />}
            {activeStep === 1 && (
              <CompanyDetailsBusinessInABox
                business={business}
                onBackClick={onBackClick}
              />
            )}
            {activeStep === 2 && accData != null && (
              <AddressInformation
                onBackClick={onBackClick}
                account={accData}
                business={business}
                hasOwnershipSection={isMultiMemberOwnedBusiness}
                onContinueToAccountVerification={
                  onContinueToAccountVerification
                }
              />
            )}
            {activeStep === 3 && accData?.id && isMultiMemberOwnedBusiness && (
              <OwnershipDetails
                account={accData}
                business={business}
                onBackClick={onBackClick}
                onContinueToAccountVerification={
                  onContinueToAccountVerification
                }
              />
            )}
            {activeStep === 3 && accData?.id && !isMultiMemberOwnedBusiness && (
              <FilingDocumentsBusinessInABox
                accountId={accData?.id}
                businessData={business}
                completedSteps={completed}
                stageCompleted={
                  state[ProgressTrackerStages.FilingDocuments]?.status ===
                  ProgressTrackerStatus.Completed
                }
                onContinueToAccountVerification={
                  onContinueToAccountVerification
                }
                onBackClick={onBackClick}
              />
            )}
            {activeStep === 4 && accData?.id && isMultiMemberOwnedBusiness && (
              <FilingDocumentsBusinessInABox
                accountId={accData?.id}
                businessData={business}
                completedSteps={completed}
                stageCompleted={
                  state[ProgressTrackerStages.FilingDocuments]?.status ===
                  ProgressTrackerStatus.Completed
                }
                onContinueToAccountVerification={
                  onContinueToAccountVerification
                }
                onBackClick={onBackClick}
              />
            )}
          </StepContentWrapper>
        </Grid>
      </AccordionDetails>
    </Accordion>
  );
};

export const IncorporationBusinessInABox = (props: {
  isDesktop: boolean;
  expanded: boolean | string;
  handleExpand: (
    panel: string,
  ) => (event: React.ChangeEvent<{}>, isExpanded: boolean) => void;
  onContinueToAccountVerification: () => void;
}) => {
  const {
    currentCompany: businessData,
    isLoading: businessDataLoading,
    status: businessDataStatus,
  } = useCurrentCompany();
  const {
    currentAccount: accountData,
    isLoading: accountDataLoading,
    status: accountDataStatus,
  } = useCurrentAccount();

  return !businessDataLoading &&
    businessDataStatus === 'success' &&
    businessData &&
    !accountDataLoading &&
    accountDataStatus === 'success' &&
    accountData ? (
    <IncorporationLayout
      {...props}
      business={businessData}
      account={accountData}
    />
  ) : (
    <StageSkeleton />
  );
};
